import { useEffect, useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@supabase/auth-helpers-react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Receipt } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

interface PaymentRecord {
  id: string;
  date: string;
  amount: number;
  status: string;
  type: 'subscription' | 'donation';
  description: string;
  invoice_url?: string;
}

export default function BillingHistory() {
  const session = useSession();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!session) {
      navigate("/premium");
      return;
    }
    fetchPaymentHistory();
  }, [session, navigate]);

  const fetchPaymentHistory = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoading(true);

      // Fetch subscription payments
      const { data: subscriptions } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      const allPayments: PaymentRecord[] = [];

      // Process subscriptions
      if (subscriptions) {
        subscriptions.forEach((sub) => {
          allPayments.push({
            id: sub.id,
            date: sub.created_at,
            amount: sub.status === 'premium' ? 4.99 : sub.status === 'church' ? 49.99 : 0,
            status: sub.status,
            type: 'subscription',
            description: `${sub.status === 'church' ? 'Church' : 'Premium'} Subscription`,
          });
        });
      }

      // Sort all payments by date
      allPayments.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      setPayments(allPayments);
    } catch (error: any) {
      console.error('Error fetching payment history:', error);
      toast({
        title: "Error",
        description: "Failed to load payment history",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      premium: { label: 'Active', variant: 'default' },
      church: { label: 'Active', variant: 'default' },
      free: { label: 'Canceled', variant: 'secondary' },
      completed: { label: 'Completed', variant: 'default' },
    };

    const config = statusConfig[status] || { label: status, variant: 'outline' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (!session) return null;

  return (
    <div className="min-h-screen bg-app-background transition-colors duration-300 pb-24">
      <div className="container py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="text-app-text-primary hover:bg-app-surface"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-semibold text-app-text-primary">Billing History</h1>
            <p className="text-app-text-secondary mt-1">View all your past payments and transactions</p>
          </div>
        </div>

        <Card className="border-app-border bg-app-surface">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-app-text-primary">
              <Receipt className="w-5 h-5" />
              Payment History
            </CardTitle>
            <CardDescription className="text-app-text-secondary">
              A complete record of all your payments and subscriptions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : payments.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="w-12 h-12 mx-auto text-app-text-secondary mb-4 opacity-50" />
                <h3 className="text-lg font-medium text-app-text-primary mb-2">No Payment History</h3>
                <p className="text-app-text-secondary mb-6">
                  You haven't made any payments yet. Subscribe to a plan to get started.
                </p>
                <Button onClick={() => navigate('/premium')} className="bg-app-accent text-app-accent-foreground">
                  View Plans
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-app-border">
                      <TableHead className="text-app-text-secondary">Date</TableHead>
                      <TableHead className="text-app-text-secondary">Description</TableHead>
                      <TableHead className="text-app-text-secondary">Type</TableHead>
                      <TableHead className="text-app-text-secondary">Amount</TableHead>
                      <TableHead className="text-app-text-secondary">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} className="border-app-border">
                        <TableCell className="text-app-text-primary font-medium">
                          {formatDate(payment.date)}
                        </TableCell>
                        <TableCell className="text-app-text-primary">
                          {payment.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="border-app-border text-app-text-secondary capitalize">
                            {payment.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-app-text-primary font-semibold">
                          {formatAmount(payment.amount)}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(payment.status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Navigation />
    </div>
  );
}
